import { useState, useEffect, useRef, useCallback } from "react";
import {
  Menu,
  X,
  Ticket,
  Home,
  Sparkles,
  Coffee,
  Gamepad2,
  PlaySquare,
  Calendar,
  Mail,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";

// Removing labels from here to evaluate them dynamically inside the component via translations
const NAV_LINKS_BASE = [
  { href: "/#home", sectionId: "home", key: "home", Icon: Home },
  { href: "/#sobre", sectionId: "sobre", key: "atmosphere", Icon: Sparkles },
  { href: "/#bar", sectionId: "bar", key: "bar", Icon: Coffee },
  { href: "/#maquinas", sectionId: "maquinas", key: "machines", Icon: Gamepad2 },
  { href: "/#midia", sectionId: "midia", key: "media", Icon: PlaySquare },
  { href: "/#eventos", sectionId: "eventos", key: "events", Icon: Calendar },
  { href: "/#ingressos", sectionId: "ingressos", key: "tickets", Icon: Ticket },
  { href: "/#contato", sectionId: "contato", key: "contact", Icon: Mail },
];

type NavbarProps = {
  activeSection?: string;
  className?: string;
};

export default function Navbar({ activeSection = "home", className = "" }: NavbarProps) {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [overflowed, setOverflowed] = useState(false);

  // Refs for overflow detection
  const barRef = useRef<HTMLDivElement>(null);
  const linksRef = useRef<HTMLDivElement>(null);

  const [isTVRoute] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.location.pathname === "/tv" || window.location.hostname.startsWith("tv.");
  });

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 60);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Dynamic overflow detection - switches to hamburger when items don't fit
  const checkOverflow = useCallback(() => {
    // Defer measurement to next frame to batch reads/writes and avoid forced reflow
    requestAnimationFrame(() => {
      const bar = barRef.current;
      const links = linksRef.current;
      if (!bar || !links) return;

      // Temporarily show links to measure their natural width
      const wasHidden = links.style.display;
      links.style.display = "flex";
      links.style.visibility = "hidden";
      links.style.position = "absolute";

      // Total width consumed by: logo (~60px gap) + links + right side elements
      const logoEl = bar.querySelector("[data-nav-logo]") as HTMLElement | null;
      const rightEl = bar.querySelector(
        "[data-nav-right]",
      ) as HTMLElement | null;

      const logoWidth = logoEl ? logoEl.offsetWidth + 16 : 80; // 16px gap
      const rightWidth = rightEl ? rightEl.offsetWidth + 16 : 200;
      const linksWidth = links.scrollWidth;
      const availableWidth = bar.clientWidth;

      // Add a safety margin (24px) to prevent any visual collision
      const totalNeeded = logoWidth + linksWidth + rightWidth + 24;

      // Restore original state
      links.style.display = wasHidden;
      links.style.visibility = "";
      links.style.position = "";

      setOverflowed(totalNeeded > availableWidth);
    });
  }, []);

  // Observe for resize and language changes
  useEffect(() => {
    checkOverflow();

    // O ResizeObserver disparava várias vezes seguidas no carregamento — a
    // barra muda de largura quando as fontes web trocam a métrica do texto, e
    // CADA disparo remedia e podia alternar menu <-> hambúrguer. Somado ao
    // salto do --r-scale (resolvido no index.html), era o reposicionamento em
    // etapas que se via na navbar. Com o debounce, a barra assenta uma vez.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const ro = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(checkOverflow, 120);
    });

    if (barRef.current) {
      ro.observe(barRef.current);
    }

    // As fontes chegam depois do primeiro layout (fonts.css entra com
    // media="print"): quando trocam, a largura dos links muda. Uma remedição
    // dedicada aqui é mais barata e precisa do que deixar o observer
    // perseguindo o resultado.
    let vivo = true;
    document.fonts?.ready.then(() => {
      if (vivo) checkOverflow();
    });

    return () => {
      vivo = false;
      clearTimeout(timer);
      ro.disconnect();
    };
  }, [checkOverflow]);

  // Re-check when language changes (translated labels may have different widths)
  useEffect(() => {
    // Small delay to let the DOM update with new text
    const timer = setTimeout(checkOverflow, 50);
    return () => clearTimeout(timer);
  }, [i18n.language, checkOverflow]);

  return (
    <nav
      aria-label="Main navigation"
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 will-change-transform transform-gpu ${scrolled
        ? "bg-pinball-black/90 backdrop-blur-md shadow-lg shadow-pinball-red/10"
        : "bg-gradient-to-b from-black/80 via-black/40 to-transparent"
        } ${className}`}
      style={{ transform: "translateZ(0)" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={barRef}
          className="flex items-center justify-between h-16 flex-nowrap whitespace-nowrap"
        >
          {/* Logo */}
          <a
            href="/#home"
            className="flex items-center gap-2 group flex-shrink-0 relative h-16 w-36 sm:w-48 md:w-52 lg:w-60"
            data-nav-logo
          >
            <img
              src="/images/oldschool-plus-neon.svg"
              alt="Oldschool.plus"
              className="absolute left-0 top-1/2 -translate-y-1/2 h-12 w-auto max-w-none object-contain bg-transparent
                group-hover:brightness-125 transition-all duration-300"
            />
          </a>

          {/* Desktop Nav - hidden when overflowed or on TV route */}
          <div
            ref={linksRef}
            className={`items-center gap-1 flex-nowrap whitespace-nowrap overflow-hidden ${(overflowed || isTVRoute) ? "hidden" : "flex"
              }`}
          >
            {NAV_LINKS_BASE.map((link) => {
              const isActive = activeSection === link.sectionId;
              return (
                <a
                  key={link.href}
                  href={link.href}
                  className={`relative px-1.5 lg:px-2 xl:px-3 py-2 font-tech text-[11px] lg:text-xs xl:text-sm transition-colors duration-300 group ${isActive
                    ? "text-pinball-red"
                    : "text-pinball-cream/80 hover:text-white"
                    }`}
                >
                  {t(`navbar.${link.key}`)}
                  <span
                    className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-pinball-red rounded-full transition-all duration-300 ${isActive ? "w-4/5" : "w-0 group-hover:w-4/5"
                      }`}
                  />
                </a>
              );
            })}
          </div>

          {/* Right - CTA */}
          <div
            className="flex items-center gap-1 lg:gap-2 flex-nowrap whitespace-nowrap flex-shrink-0"
            data-nav-right
          >
            <div className={`${isTVRoute ? "hidden" : "flex items-center"}`}>
              <LanguageSwitcher />
            </div>

            {/* Navegação INTERNA do próprio site: "/" e "/tv" são rotas deste
                projeto (mesmo domínio, oldschool.plus). Antes os dois botões
                apontavam para https://stream.builders — o site da empresa —
                então o botão TV saía do produto e o de volta não voltava. */}
            {isTVRoute ? (
              <a
                href="/"
                className={`btn-retro btn-retro-nav gap-1.5 !border-pinball-red !shadow-[0_0_15px_rgba(255,42,42,0.4)] hover:!shadow-[0_0_25px_rgba(255,42,42,0.8)] !text-pinball-cream hover:!bg-pinball-red/10 !inline-flex`}
              >
                <Home className="w-4 h-4" />
                <span>Oldschool.plus</span>
              </a>
            ) : (
              <a
                href="/tv"
                className={`btn-retro btn-retro-nav gap-1.5 !border-pinball-neon-blue !shadow-[0_0_15px_rgba(0,183,255,0.4)] hover:!shadow-[0_0_25px_rgba(0,183,255,0.8)] !text-pinball-neon-blue hover:!bg-pinball-neon-blue/10 ${overflowed ? "!hidden" : "!inline-flex"}`}
              >
                <PlaySquare className="w-4 h-4" />
                <span className="hidden xl:inline-block">Linear TV</span>
                <span className="inline-block xl:hidden">TV</span>
              </a>
            )}

            {/* Mobile hamburger - visible when overflowed, but hidden on TV route because there's no page navigation needed */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`p-2 text-pinball-cream ${overflowed && !isTVRoute ? "" : "hidden"}`}
              aria-label={t("navbar.menu")}
            >
              {mobileOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer - visible when overflowed */}
      <div
        className={`fixed left-0 right-0 top-16 h-[calc(100svh-4rem)] bg-pinball-black/95 backdrop-blur-lg border-t border-pinball-red/20 overflow-y-auto transition-all duration-300 ${!overflowed ? "hidden" : ""
          } ${mobileOpen
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-8 pointer-events-none"
          }`}
      >
        <div className="flex flex-col items-center justify-start py-6 px-6 space-y-2">
          {NAV_LINKS_BASE.map((link) => {
            const isActive = activeSection === link.sectionId;
            const Icon = link.Icon;
            return (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center justify-center w-full max-w-[250px] py-2 px-4 font-tech rounded-lg transition-colors duration-200 ${isActive
                  ? "text-pinball-red bg-pinball-red/10"
                  : "text-pinball-cream/80 hover:text-pinball-yellow hover:bg-pinball-red/10"
                  }`}
              >
                <Icon className="w-5 h-5 mr-3 opacity-80" />
                <span>{t(`navbar.${link.key}`)}</span>
              </a>
            );
          })}
          <div className="w-full max-w-[250px] pt-4 mt-2 border-t border-pinball-red/20">
            {isTVRoute ? (
              <a
                href="/"
                className="!flex w-full justify-center btn-retro text-center text-xs py-2 mt-2 !border-pinball-neon-blue !text-pinball-neon-blue hover:!bg-pinball-neon-blue/10"
              >
                <Home className="w-5 h-5 mr-2 inline-block" />
                Oldschool.plus
              </a>
            ) : (
              <a
                href="/tv"
                className="!flex w-full justify-center btn-retro text-center text-xs py-2 mt-2 !border-pinball-neon-blue !text-pinball-neon-blue hover:!bg-pinball-neon-blue/10"
              >
                <PlaySquare className="w-5 h-5 mr-2 inline-block" />
                Linear TV
              </a>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
